import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronRightIcon,
  HomeIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
  TruckIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline'
import { BreadcrumbItem } from '../../../types/layout'

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
}

/**
 * Componente para mostrar breadcrumbs de navegación
 * Diseño minimalista con mejor UX y efectos suaves
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation()

  // Iconos para diferentes rutas
  const RouteIcon = useMemo(() => {
    const path = location.pathname.toLowerCase()
    if (path.includes('orders')) return BuildingStorefrontIcon
    if (path.includes('products')) return ArchiveBoxIcon
    if (path.includes('settings')) return WrenchScrewdriverIcon
    if (path.includes('profile')) return UserCircleIcon
    if (path.includes('envios')) return TruckIcon
    if (path.includes('cotizaciones')) return DocumentTextIcon
    if (path.includes('erp')) return PuzzlePieceIcon
    return null
  }, [location.pathname])

  if (items.length === 0) return null

  return (
    <nav
      className="flex items-center gap-1.5 text-xs mt-1"
      aria-label="Breadcrumb"
    >
      {/* Inicio */}
      <Link
        to="/home"
        className="flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 
                   hover:text-custom-blue hover:bg-blue-50 
                   transition-all duration-200 group"
        aria-label="Ir a inicio"
      >
        <HomeIcon className="h-3.5 w-3.5 group-hover:text-custom-blue transition-colors" />
        <span className="hidden sm:inline">Inicio</span>
      </Link>

      {/* Breadcrumbs */}
      {items.map((crumb, index) => {
        const isLast = crumb.isLast

        return (
          <React.Fragment key={`${crumb.to}-${index}`}>
            {/* Separador */}
            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />

            {/* Item del breadcrumb */}
            {isLast ? (
              <span
                className="px-2 py-1 text-gray-900 font-medium flex items-center gap-1.5"
                aria-current="page"
              >
                {RouteIcon && (
                  <RouteIcon className="h-3.5 w-3.5 text-custom-blue flex-shrink-0" />
                )}
                <span className="truncate max-w-[150px] sm:max-w-none">
                  {crumb.label}
                </span>
              </span>
            ) : (
              <Link
                to={crumb.to}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 
                           hover:text-custom-blue hover:bg-blue-50 
                           transition-all duration-200 group truncate max-w-[120px] sm:max-w-none"
              >
                <span className="truncate">{crumb.label}</span>
              </Link>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default React.memo(Breadcrumbs)

